import React, { useContext } from "react";
import { Modal, Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { AppContext } from "../../../context/AppContextProvider";

import AddonAction from "./AddonAction";
import {
  getAddonBlockedReasonKey,
  getAddonDependencyNames,
  getAddonDescription,
  getAddonImage,
  getAddonName,
  getAddonPriceValue,
  getAddonRequiredByNames,
  isAddonFree,
} from "./addonPresentation";

import "./addons-showcase.css";

const TagGroup = ({ label, names }) => {
  if (!names?.length) return null;
  return (
    <div className="addon-preview-modal__tag-group">
      <div className="addon-preview-modal__tag-label">{label}</div>
      <div className="addon-preview-modal__tag-list">
        {names.map((name) => (
          <Tag key={name} className="addon-preview-modal__tag">
            {name}
          </Tag>
        ))}
      </div>
    </div>
  );
};

const AddonPreviewModal = ({
  addon,
  open,
  onClose,
  reloadCallback,
  actionLoading = false,
  onActionLoadingChange,
}) => {
  const { appConfig, authUser } = useContext(AppContext);
  const { t } = useTranslation();

  const isRtl = appConfig?.direction === "rtl";
  const priceValue = getAddonPriceValue(addon);
  const freeAddon = isAddonFree(addon);
  const addonName = getAddonName(addon, isRtl);
  const addonDescription = getAddonDescription(addon, isRtl);
  const dependencyNames = getAddonDependencyNames(addon, isRtl);
  const requiredByNames = getAddonRequiredByNames(addon, isRtl);
  const blockedReasonKey = getAddonBlockedReasonKey(
    addon,
    authUser?.active_addons || [],
  );
  const blockedReason = blockedReasonKey ? t(blockedReasonKey) : "";
  const trialDays = Number(addon?.trial_validity_days) || 0;

  const highlightItems = [
    addon?.is_subscribed
      ? t("addon_currently_active")
      : t("addon_ready_to_activate"),
    freeAddon ? t("addon_free_access") : t("addon_daily_billed_module"),
    trialDays > 0
      ? t("addon_free_trial_days", { count: trialDays, days: trialDays })
      : t("addon_no_trial_period"),
    dependencyNames.length > 0
      ? t("addon_required_addons_count", { count: dependencyNames.length })
      : t("addon_no_dependency_required"),
  ];

  return (
    <Modal
      open={open}
      onCancel={() => !actionLoading && onClose?.()}
      footer={null}
      centered
      width={640}
      className="addon-preview-modal"
      closeIcon={actionLoading ? null : <CloseOutlined />}
      closable={!actionLoading}
      keyboard={!actionLoading}
      maskClosable={!actionLoading}
      styles={{
        mask: {
          backdropFilter: "blur(10px)",
          backgroundColor: "var(--color-shadow)",
        },
      }}
    >
      {addon && (
        <div
          className="addon-preview-modal__content"
          dir={isRtl ? "rtl" : "ltr"}
        >
          {/* ── Hero ── */}
          <div className="addon-preview-modal__hero">
            <img
              alt={addonName}
              src={getAddonImage(addon)}
              className="addon-preview-modal__hero-image"
            />
            <div className="addon-preview-modal__hero-gradient" />

            <div className="addon-preview-modal__hero-badges">
              <Tag
                className={`addon-showcase-status ${addon?.is_subscribed ? "is-active" : "is-available"}`}
              >
                {addon?.is_subscribed ? t("active") : t("addon_available")}
              </Tag>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="addon-preview-modal__body">
            <div className="addon-preview-modal__header">
              <div className="addon-preview-modal__title">{addonName}</div>
            </div>

            <div className="addon-preview-modal__description">
              {addonDescription}
            </div>

            {/* Feature highlights */}
            <div className="addon-preview-modal__features-label">
              {t("addon_whats_included")}
            </div>
            <div className="addon-preview-modal__features-grid">
              {highlightItems.map((item) => (
                <div className="addon-preview-modal__feature-item" key={item}>
                  <span className="addon-preview-modal__feature-dot" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <TagGroup label={t("addon_requires")} names={dependencyNames} />
            <TagGroup label={t("addon_used_by")} names={requiredByNames} />

            {blockedReason && (
              <div className="addon-showcase-conflict">{blockedReason}</div>
            )}

            {/* Price + trial panel */}
            <div className="addon-preview-modal__price-row">
              <div>
                <div className="addon-preview-modal__price-label">
                  {freeAddon
                    ? t("addon_access")
                    : t("addon_daily_price_after_trial")}
                </div>
                <div className="addon-preview-modal__price-value">
                  {freeAddon ? (
                    t("free")
                  ) : (
                    <CurrencyFormatter value={priceValue} />
                  )}
                </div>
                <div className="addon-preview-modal__price-subtitle">
                  {freeAddon
                    ? t("addon_available_with_current_setup")
                    : t("addon_charged_only_when_in_use")}
                </div>
              </div>

              <div className="addon-preview-modal__trial-panel">
                <div className="addon-preview-modal__trial-value">
                  {trialDays > 0 ? trialDays : freeAddon ? t("free") : "0"}
                </div>
                <div className="addon-preview-modal__trial-label">
                  {trialDays > 0
                    ? t("addon_trial_days_label")
                    : freeAddon
                      ? t("addon_always_on")
                      : t("trial_days")}
                </div>
              </div>
            </div>

            <AddonAction
              addonData={addon}
              reloadCallback={reloadCallback}
              variant="modal"
              onActionLoadingChange={onActionLoadingChange}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddonPreviewModal;
